import React, { useRef, useEffect, useState } from 'react';

interface Props {
  onSend: (text: string) => void;
  disabled?: boolean;
}

const MAX_ROWS = 5;

const ChatComposer: React.FC<Props> = ({ onSend, disabled = false }) => {
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea up to MAX_ROWS lines
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const lineHeight = 22; // px – matches text-sm leading
    const maxHeight = lineHeight * MAX_ROWS + 20; // padding
    el.style.height = Math.min(el.scrollHeight, maxHeight) + 'px';
  }, [value]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setValue('');
    // Reset height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  return (
    <div className="flex items-end gap-2 px-3 py-3 border-t border-gray-200 bg-white">
      <textarea
        ref={textareaRef}
        id="chat-composer-textarea"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        placeholder="Ask anything about this topic…"
        rows={1}
        className="flex-1 resize-none rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5624d0]/30 focus:border-[#5624d0] transition disabled:opacity-50 leading-[22px]"
        style={{ minHeight: '42px', maxHeight: `${22 * MAX_ROWS + 20}px`, overflowY: 'auto' }}
        aria-label="Chat message input"
      />

      <button
        id="chat-send-button"
        onClick={submit}
        disabled={disabled || !value.trim()}
        className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#5624d0] hover:bg-[#401b9c] disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center justify-center transition-all duration-150 shadow-md"
        aria-label="Send message"
        title="Send (Enter)"
      >
        {disabled ? (
          // Spinner while sending
          <svg
            className="w-4 h-4 animate-spin"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            />
          </svg>
        ) : (
          <svg
            className="w-4 h-4 translate-x-0.5"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
          </svg>
        )}
      </button>
    </div>
  );
};

export default ChatComposer;
